import React, { useState, useEffect } from 'react';
import { Star, ChevronDown, RefreshCw, X, CreditCard, RefreshCcw } from 'lucide-react';
import { useUsageLimiter } from '../../utils/usageLimiter';
import StarCoinsModal from './StarCoinsModal';
import { motion, AnimatePresence } from 'framer-motion';
import { starCoinsClient } from '../../services/starCoinsClient';
import { detectUserCurrency, CURRENCY_INFO, calculatePrice, setUserCurrency } from '../../utils/detectLanguage';
import { trackInitiateStarCoinPurchase } from '../../utils/pixels';
import { formatStarCoins, formatStarCoinsCompact } from './formatStarCoins';

const StarCoinsDisplay = ({ userId, isMobile = false, compact = false, hideButton = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { starCoins, tier } = useUsageLimiter(userId);
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // ADD CURRENCY STATES FOR MOBILE
  const [currency, setCurrency] = useState('usd');
  const [showCurrencyOption, setShowCurrencyOption] = useState(false);

  // Add state for mobile tab switching and transactions
  const [showMobileTransactions, setShowMobileTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  useEffect(() => {
    let isMounted = true;
    
    if (userId) {
      setIsLoading(true);
      starCoins.loadBalance().finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, [userId]);

  // Listen for custom event to open modal
  useEffect(() => {
    const handleOpenModal = () => {
      console.log('openStarCoinsModal event received in StarCoinsDisplay');
      setIsModalOpen(true);
    };

    console.log('StarCoinsDisplay: Adding event listener for openStarCoinsModal');
    window.addEventListener('openStarCoinsModal', handleOpenModal);

    return () => {
      console.log('StarCoinsDisplay: Removing event listener for openStarCoinsModal');
      window.removeEventListener('openStarCoinsModal', handleOpenModal);
    };
  }, []);

  useEffect(() => {
    if (isModalOpen && isMobile) {
      // Detect user's preferred currency based on locale
      const detectedCurrency = detectUserCurrency();
      setShowCurrencyOption(true); // Always show currency selector
      setCurrency(detectedCurrency);

      starCoinsClient.getPackages(tier === 'pro')
        .then(packages => {
          setPackages(packages);
          if (packages.length > 0) {
            setSelectedPackage(packages[0].id);
          }
        })
        .catch(error => {
          console.error('Error loading packages:', error);
          setError('Failed to load packages. Please try again.');
        });

      // Load transactions for mobile view
      starCoinsClient.getTransactionHistory()
        .then(txns => setTransactions(txns))
        .catch(error => {
          console.error('Error loading transactions:', error);
        });
    }
  }, [isModalOpen, isMobile, tier]);

  // Handle star coin purchase for mobile view
  const handleMobilePurchase = async () => {
    if (!selectedPackage) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const packageData = packages.find(p => p.id === selectedPackage);
      if (!packageData) {
        throw new Error('Package not found');
      }

      // Calculate price in selected currency (uses Firestore custom price or fallback calculation)
      const price = calculatePrice(packageData, currency);

      // Track initiate checkout event for Facebook Pixel
      trackInitiateStarCoinPurchase(packageData.id, price);
      
      // ADD CURRENCY PARAMETER
      await starCoinsClient.createCheckout(
        packageData.id,
        packageData.amount,
        price, // Use converted price
        tier === 'pro',
        currency // ADD CURRENCY PARAMETER
      );
    } catch (error) {
      console.error('Error processing payment:', error);
      setIsProcessing(false);
      setError('Error processing payment. Please try again.');
    }
  };
  
  if (isMobile || compact) {
    return (
      <>
        {!hideButton && (
          <button
            onClick={() => setIsModalOpen(!isModalOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 active:scale-95 relative group"
            aria-label={`Star Coins: ${isLoading ? 'Loading...' : starCoins.balance}`}
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 text-white/40 animate-spin" />
            ) : (
              <div className="relative">
                <Star className="w-4 h-4 text-violet-400/70 fill-violet-400/20 group-hover:text-violet-400/90 group-hover:fill-violet-400/30 transition-all" />
                {/* Subtle badge showing balance - positioned lower */}
                <div className="absolute -bottom-2 -right-1 bg-gradient-to-r from-violet-500/80 to-violet-600/80 text-white text-[8px] font-mono font-medium rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 border border-black/20">
                  {formatStarCoinsCompact(starCoins.balance)}
                </div>
              </div>
            )}
          </button>
        )}

        {/* Mobile modal renders regardless of hideButton prop */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-50"
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ y: '-100%' }}
                animate={{ y: 0 }}
                exit={{ y: '-100%' }}
                transition={{
                  type: 'spring',
                  damping: 25,
                  stiffness: 300,
                  mass: 0.8
                }}
                className="bg-black/90 backdrop-blur-lg border-b border-white/10 shadow-xl w-full fixed top-0 left-0 right-0 rounded-b-lg"
                onClick={e => e.stopPropagation()}
                style={{ maxHeight: '90vh', overflowY: 'auto' }}
              >
                <div className="flex items-center justify-between p-3 border-b border-white/[0.05] sticky top-0 bg-black/90 backdrop-blur-lg z-10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded flex items-center justify-center">
                      <Star className="w-4 h-4 text-emerald-400/80" />
                    </div>
                    <h3 className="text-[11px] font-medium tracking-[0.2em] text-white/70 uppercase">Star Coins</h3>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white/70 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-3 pb-6">
                  {/* Tab buttons */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setShowMobileTransactions(false)}
                      className={`flex-1 py-2 px-3 rounded-md text-[10px] font-medium uppercase tracking-[0.15em] transition-all duration-200 ${
                        !showMobileTransactions
                          ? 'bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-white/80 border border-emerald-500/30'
                          : 'bg-white/[0.02] text-white/40 border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'
                      }`}
                    >
                      <CreditCard className="w-3 h-3 inline mr-1" />
                      Purchase
                    </button>
                    <button
                      onClick={() => setShowMobileTransactions(true)}
                      className={`flex-1 py-2 px-3 rounded-md text-[10px] font-medium uppercase tracking-[0.15em] transition-all duration-200 ${
                        showMobileTransactions
                          ? 'bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-white/80 border border-emerald-500/30'
                          : 'bg-white/[0.02] text-white/40 border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'
                      }`}
                    >
                      <RefreshCcw className="w-3 h-3 inline mr-1" />
                      History
                    </button>
                  </div>

                  <div className="mb-3 p-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-white/50 uppercase tracking-wider">{currency === 'brl' ? 'Saldo' : 'Balance'}</span>
                      <div className="flex items-center gap-1.5 font-mono text-sm">
                        <Star className="w-3.5 h-3.5 text-emerald-400/80" />
                        <span className="font-bold text-white/70">{formatStarCoins(starCoins.balance || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Show purchase packages or transactions based on tab */}
                  {!showMobileTransactions ? (
                    <>
                      {/* Currency Selection - Professional Flag Buttons (Mobile) */}
                      <div className="mb-3 p-2.5 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                        <h4 className="text-[9px] text-white/40 uppercase tracking-wider mb-2 text-center">Currency</h4>
                        <div className="grid grid-cols-3 gap-1.5">
                          <button
                            onClick={() => { setCurrency('usd'); setUserCurrency('usd'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'usd'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇺🇸</span>
                            <span className="text-[10px] font-medium">USD</span>
                          </button>
                          <button
                            onClick={() => { setCurrency('brl'); setUserCurrency('brl'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'brl'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇧🇷</span>
                            <span className="text-[10px] font-medium">BRL</span>
                          </button>
                          <button
                            onClick={() => { setCurrency('mxn'); setUserCurrency('mxn'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'mxn'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇲🇽</span>
                            <span className="text-[10px] font-medium">MXN</span>
                          </button>
                          <button
                            onClick={() => { setCurrency('ars'); setUserCurrency('ars'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'ars'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇦🇷</span>
                            <span className="text-[10px] font-medium">ARS</span>
                          </button>
                          <button
                            onClick={() => { setCurrency('cop'); setUserCurrency('cop'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'cop'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇨🇴</span>
                            <span className="text-[10px] font-medium">COP</span>
                          </button>
                          <button
                            onClick={() => { setCurrency('clp'); setUserCurrency('clp'); }}
                            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-all ${
                              currency === 'clp'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-white/[0.03] text-white/60 border border-white/[0.1] hover:bg-white/[0.05]'
                            }`}
                          >
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 overflow-hidden text-xs">🇨🇱</span>
                            <span className="text-[10px] font-medium">CLP</span>
                          </button>
                        </div>
                        <div className="mt-1.5 text-[9px] text-white/40 text-center">
                          {currency === 'brl' && 'PIX + Cards'}
                          {currency === 'mxn' && 'Cards Only'}
                          {!['brl', 'mxn'].includes(currency) && 'Cards Only'}
                        </div>
                      </div>

                  {error && (
                    <div className="mb-3 p-2.5 bg-red-500/10 border border-red-500/20 rounded-md">
                      <p className="text-red-400/80 text-[11px]">{error}</p>
                    </div>
                  )}

                  <div className="space-y-2 mb-3">
                    {packages.map(pkg => {
                      const isActive = selectedPackage === pkg.id;
                      const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;

                      // Calculate display price for this package
                      const displayPrice = calculatePrice(pkg, currency);
                      const currencySymbol = CURRENCY_INFO[currency]?.symbol || '$';

                      return (
                        <div
                          key={pkg.id}
                          className={`p-3 rounded-md cursor-pointer transition-all duration-200 ${
                            isActive
                              ? 'bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/30'
                              : 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'
                          }`}
                          onClick={() => setSelectedPackage(pkg.id)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="flex items-center font-mono text-sm">
                                <Star className="w-3.5 h-3.5 mr-1.5 text-emerald-400/80" />
                                <span className="font-bold text-white/70">{formatStarCoins(pkg.amount)}</span>
                                <span className="ml-1 text-emerald-400/80 font-medium">{currency === 'brl' ? 'moedas' : 'coins'}</span>
                              </div>
                              {bonusAmount > 0 && (
                                <div className="text-[10px] font-mono mt-0.5">
                                  <span className="font-bold text-white/70">+{formatStarCoins(Math.round(bonusAmount))}</span>
                                  <span className="ml-1 text-emerald-400/70">{currency === 'brl' ? 'bônus' : 'bonus'}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              {/* PROMOTIONAL STRIKETHROUGH - Remove this block to disable promotion */}
                              {currency === 'brl' && pkg.brlPrice && pkg.brlPrice === 2 && (
                                <>
                                  <div className="text-white/30 text-[10px] font-mono line-through">R$5,00</div>
                                  <div className="text-emerald-400/70 text-[9px] uppercase tracking-wider">Até 15 de Jan</div>
                                </>
                              )}
                              {/* END PROMOTIONAL STRIKETHROUGH */}

                              <div className="text-white/80 font-mono font-medium text-sm">{currencySymbol}{displayPrice.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={handleMobilePurchase}
                    disabled={!selectedPackage || isProcessing}
                    className="w-full py-3 rounded-md flex items-center justify-center gap-2 text-white font-medium text-[11px] uppercase tracking-[0.15em] bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-emerald-500/10"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCcw className="w-4 h-4 animate-spin" />
                        <span>{currency === 'brl' ? 'Processando...' : 'Processing...'}</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>{currency === 'brl' ? 'Comprar Coins' : 'Purchase Coins'}</span>
                      </>
                    )}
                  </button>

                      {/* Secure checkout text */}
                      <p className="text-center text-[10px] text-white/30 uppercase tracking-wider mt-2">
                        {currency === 'brl' ? 'Checkout seguro via Stripe' : 'Secure checkout via Stripe'}
                      </p>
                    </>
                  ) : (
                    /* Transaction History View */
                    <div className="space-y-2">
                      {transactions.length === 0 ? (
                        <div className="text-center py-8">
                          <RefreshCcw className="w-8 h-8 text-white/20 mx-auto mb-3" />
                          <p className="text-white/50 text-xs">No transactions yet</p>
                          <p className="text-white/30 text-[10px] uppercase tracking-wider mt-1">Purchase coins to see history</p>
                        </div>
                      ) : (
                        <>
                          {transactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((transaction, index) => (
                            <div key={index} className="p-3 rounded-md bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-white/70 text-[11px]">{transaction.feature || 'Star Coins'}</div>
                                  <div className="text-white/30 text-[10px] uppercase tracking-wider mt-0.5">
                                    {new Date(transaction.timestamp?.toDate ? transaction.timestamp.toDate() : transaction.timestamp).toLocaleDateString()}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className={`font-mono font-medium text-xs ${transaction.amount > 0 ? 'text-emerald-400/80' : 'text-red-400/70'}`}>
                                    {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                                  </div>
                                  <div className="text-white/30 text-[10px] font-mono mt-0.5">
                                    {formatStarCoins(transaction.balance)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Pagination for transactions */}
                          {transactions.length > itemsPerPage && (
                            <div className="flex items-center justify-center gap-2 pt-3 border-t border-white/[0.05]">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-2 py-1 rounded text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                ← Prev
                              </button>
                              <span className="text-white/40 text-[10px] font-mono">
                                {currentPage} / {Math.ceil(transactions.length / itemsPerPage)}
                              </span>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(transactions.length / itemsPerPage), prev + 1))}
                                disabled={currentPage >= Math.ceil(transactions.length / itemsPerPage)}
                                className="px-2 py-1 rounded text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                Next →
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
  
  return (
    <>
      {!hideButton && (
        <button
          onClick={() => setIsModalOpen(!isModalOpen)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all bg-gradient-to-r from-amber-400/5 to-orange-500/20 text-amber-300 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/20 shadow-sm"
        >
          <Star className="w-3.5 h-3.5 text-amber-300" />
          {isLoading ? (
            <RefreshCw className="w-3.5 h-3.5 text-amber-300 animate-spin" />
          ) : (
            <span className="text-sm font-medium">{formatStarCoins(starCoins.balance)}</span>
          )}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      )}

      <StarCoinsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
      />
    </>
  );
};

export default StarCoinsDisplay;

// import React, { useState, useEffect } from 'react';
// import { Star, ChevronDown, RefreshCw, X, CreditCard, RefreshCcw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import StarCoinsModal from './StarCoinsModal';
// import { motion, AnimatePresence } from 'framer-motion';
// import { starCoinsClient } from '../../services/starCoinsClient';

// const StarCoinsDisplay = ({ userId, isMobile = false, compact = false }) => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const { starCoins, tier } = useUsageLimiter(userId);
//   const [isLoading, setIsLoading] = useState(true);
//   const [packages, setPackages] = useState([]);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [error, setError] = useState(null);
  
//   useEffect(() => {
//     let isMounted = true;
    
//     if (userId) {
//       setIsLoading(true);
//       starCoins.loadBalance().finally(() => {
//         if (isMounted) {
//           setIsLoading(false);
//         }
//       });
//     }
    
//     return () => {
//       isMounted = false;
//     };
//   }, [userId]);

//   useEffect(() => {
//     if (isModalOpen && isMobile) {
//       starCoinsClient.getPackages(tier === 'pro')
//         .then(packages => {
//           setPackages(packages);
//           if (packages.length > 0) {
//             setSelectedPackage(packages[0].id);
//           }
//         })
//         .catch(error => {
//           console.error('Error loading packages:', error);
//           setError('Failed to load packages. Please try again.');
//         });
//     }
//   }, [isModalOpen, isMobile, tier]);

//   const handleMobilePurchase = async () => {
//     if (!selectedPackage) return;
    
//     setIsProcessing(true);
//     setError(null);
    
//     try {
//       const packageData = packages.find(p => p.id === selectedPackage);
//       if (!packageData) {
//         throw new Error('Package not found');
//       }
      
//       await starCoinsClient.createCheckout(
//         packageData.id,
//         packageData.amount,
//         packageData.price,
//         tier === 'pro'
//       );
//     } catch (error) {
//       console.error('Error processing payment:', error);
//       setIsProcessing(false);
//       setError('Error processing payment. Please try again.');
//     }
//   };
  
//   if (isMobile || compact) {
//     return (
//       <>
//         <button
//           onClick={() => setIsModalOpen(!isModalOpen)}
//           className="p-2 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-300 transition-all duration-200 active:scale-95 relative group"
//           aria-label={`Star Coins: ${isLoading ? 'Loading...' : starCoins.balance}`}
//         >
//           {isLoading ? (
//             <RefreshCw className="w-5 h-5 text-amber-300 animate-spin" />
//           ) : (
//             <div className="relative">
//               <Star className="w-5 h-5 text-amber-300 fill-amber-300/50 group-hover:fill-amber-300/70 transition-all" />
//               {/* Subtle badge showing balance */}
//               <div className="absolute -bottom-1 -right-1 bg-amber-500 text-slate-900 text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
//                 {starCoins.balance > 999 ? '999+' : starCoins.balance}
//               </div>
//             </div>
//           )}
//         </button>
        
//         <AnimatePresence>
//           {isModalOpen && (
//             <motion.div
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               exit={{ opacity: 0 }}
//               className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
//               onClick={() => setIsModalOpen(false)}
//             >
//               <motion.div
//                 initial={{ y: -100, opacity: 0 }}
//                 animate={{ y: 0, opacity: 1 }}
//                 exit={{ y: -100, opacity: 0 }}
//                 className="bg-slate-800 border-b border-slate-700/50 shadow-xl w-full absolute top-14 left-0 right-0"
//                 onClick={e => e.stopPropagation()}
//               >
//                 <div className="flex items-center justify-between p-3 border-b border-slate-700/50">
//                   <div className="flex items-center gap-2">
//                     <Star className="w-4 h-4 text-amber-400" />
//                     <h3 className="text-base font-medium text-white">Star Coins</h3>
//                   </div>
//                   <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white">
//                     <X className="w-4 h-4" />
//                   </button>
//                 </div>

//                 <div className="max-h-96 overflow-y-auto p-3">
//                   <div className="mb-3 p-2 rounded-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
//                     <div className="flex items-center justify-between">
//                       <span className="text-white/70 text-xs">Balance</span>
//                       <div className="flex items-center gap-1 text-amber-300 font-medium text-sm">
//                         <Star className="w-3 h-3" />
//                         <span>{starCoins.balance || 0}</span>
//                       </div>
//                     </div>
//                   </div>

//                   {error && (
//                     <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
//                       <p className="text-red-400 text-xs">{error}</p>
//                     </div>
//                   )}

//                   <div className="space-y-1.5 mb-3">
//                     {packages.map(pkg => {
//                       const isActive = selectedPackage === pkg.id;
//                       const bonusAmount = pkg.proBonus ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;
                      
//                       return (
//                         <div 
//                           key={pkg.id}
//                           className={`p-2 rounded-lg cursor-pointer transition-all ${
//                             isActive 
//                               ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/40' 
//                               : 'bg-slate-700/30 border border-slate-600/50'
//                           }`}
//                           onClick={() => setSelectedPackage(pkg.id)}
//                         >
//                           <div className="flex justify-between items-center">
//                             <div>
//                               <div className="flex items-center text-amber-300 font-medium text-sm">
//                                 <Star className="w-3 h-3 mr-1" />
//                                 <span>{pkg.amount >= 1000 ? `${pkg.amount/1000}k` : pkg.amount} coins</span>
//                               </div>
//                               {bonusAmount > 0 && (
//                                 <div className="text-emerald-400 text-xs mt-0.5">+{Math.round(bonusAmount)} bonus</div>
//                               )}
//                             </div>
//                             <div className="text-white font-medium text-sm">${pkg.price.toFixed(2)}</div>
//                           </div>
//                         </div>
//                       );
//                     })}
//                   </div>
                  
//                   <button
//                     onClick={handleMobilePurchase}
//                     disabled={!selectedPackage || isProcessing}
//                     className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-white font-medium bg-gradient-to-r from-orange-500 to-amber-500 disabled:opacity-50 transition-all text-sm"
//                   >
//                     {isProcessing ? (
//                       <>
//                         <RefreshCcw className="w-4 h-4 animate-spin" />
//                         <span>Processing...</span>
//                       </>
//                     ) : (
//                       <>
//                         <CreditCard className="w-4 h-4" />
//                         <span>Purchase</span>
//                       </>
//                     )}
//                   </button>
//                 </div>
//               </motion.div>
//             </motion.div>
//           )}
//         </AnimatePresence>
//       </>
//     );
//   }
  
//   return (
//     <>
//       <button
//         onClick={() => setIsModalOpen(!isModalOpen)}
//         className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all bg-gradient-to-r from-amber-400/5 to-orange-500/20 text-amber-300 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/20 shadow-sm"
//       >
//         <Star className="w-3.5 h-3.5 text-amber-300" />
//         {isLoading ? (
//           <RefreshCw className="w-3.5 h-3.5 text-amber-300 animate-spin" />
//         ) : (
//           <span className="text-sm font-medium">{starCoins.balance}</span>
//         )}
//         <ChevronDown className="w-3 h-3 opacity-70" />
//       </button>
      
//       <StarCoinsModal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         userId={userId}
//       />
//     </>
//   );
// };

// export default StarCoinsDisplay;

// // Fixed StarCoinsDisplay.jsx with toggle functionality
// import React, { useState, useEffect } from 'react';
// import { Star, ChevronDown, RefreshCw } from 'lucide-react';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import StarCoinsModal from './StarCoinsModal';

// const StarCoinsDisplay = ({ userId, isMobile = false, compact = false }) => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const { starCoins } = useUsageLimiter(userId);
//   const [isLoading, setIsLoading] = useState(true);
  
//   // Only run once when the component mounts or userId changes
//   useEffect(() => {
//     let isMounted = true;
    
//     if (userId) {
//       setIsLoading(true);
      
//       // Use this pattern to prevent setting state on unmounted components
//       starCoins.loadBalance()
//         .finally(() => {
//           if (isMounted) {
//             setIsLoading(false);
//           }
//         });
//     }
    
//     // Cleanup function to prevent memory leaks
//     return () => {
//       isMounted = false;
//     };
//   }, [userId]); // Only depend on userId, NOT on starCoins
  
//   // Mobile/Compact view - just the star icon
//   if (isMobile || compact) {
//     return (
//       <>
//         <button
//           onClick={() => setIsModalOpen(!isModalOpen)} // CHANGED: Toggle instead of always true
//           className="p-2 rounded-lg hover:bg-slate-700/30 text-amber-300 transition-all duration-200 active:scale-95 relative"
//           aria-label={`Star Coins: ${isLoading ? 'Loading...' : starCoins.balance}`}
//         >
//           {isLoading ? (
//             <RefreshCw className="w-5 h-5 text-amber-300 animate-spin" />
//           ) : (
//             <Star className="w-5 h-5 text-amber-300" />
//           )}
//         </button>
        
//         <StarCoinsModal
//           isOpen={isModalOpen}
//           onClose={() => setIsModalOpen(false)}
//           userId={userId}
//         />
//       </>
//     );
//   }
  
//   // Desktop view - full display
//   return (
//     <>
//       <button
//         onClick={() => setIsModalOpen(!isModalOpen)} // CHANGED: Toggle instead of always true
//         className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all bg-gradient-to-r from-amber-400/5 to-orange-500/20 text-amber-300 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/20 shadow-sm"
//         aria-label="Star Coins Balance"
//       >
//         <Star className="w-3.5 h-3.5 text-amber-300" />
//         {isLoading ? (
//           <RefreshCw className="w-3.5 h-3.5 text-amber-300 animate-spin" />
//         ) : (
//           <span className="text-sm font-medium">{starCoins.balance}</span>
//         )}
//         <ChevronDown className="w-3 h-3 opacity-70" />
//       </button>
      
//       <StarCoinsModal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         userId={userId}
//       />
//     </>
//   );
// };

// export default StarCoinsDisplay;